import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import Button from "./Button";
import Icon from "./Icon";
import { useTranslation } from "react-i18next";

// a Context provider to manage collapse state for cards and enable collapse/expand all in a form
export const CollapseContext = createContext();

/**
 * A scoping provider used to control collapse state of all cards within it
 * @component
 */
export const CardCollapseScope = ({ children }) => {
    const [allCollapsed, setAllCollapsed] = useState(null);
    const registry = useRef(new Map()) 

    const register = useCallback((callback, collapsed) => {
        registry.current.set(callback, collapsed);
        setAllCollapsed(allCollapsed => (allCollapsed ?? true) && collapsed);
        return () => {
            registry.current.delete(callback)
            if (registry.current.size === 0) {
                setAllCollapsed(null);
            } else {
                setAllCollapsed(allCollapsed => {
                    if (!allCollapsed) {
                        return registry.current.values().every(collapsed => collapsed === true);
                    }
                    return true;
                })
            }
        };
    }, []);

    const toggleAll = useCallback(() => {
        setAllCollapsed(allCollapsed => {
            if (allCollapsed !== null) {
                for (const callback of registry.current.keys()) {
                    callback(!allCollapsed);
                }
                return !allCollapsed
            }
            return null;
        })
    }, []);

    const value = useMemo(() => ({ register, allCollapsed, toggleAll }), [register, allCollapsed, toggleAll]);

    return <CollapseContext.Provider value={value}>
        {children}
    </CollapseContext.Provider>
}

export const useCollapseManager = () => {
    return useContext(CollapseContext);
}

/**
 * A button used to toggle the collapse state of all cards inside the enclosing CardCollapseProvider
 * @component
 */
export const CardCollapseButton = ({ ...props }) => {
    let { t } = useTranslation();
    const { toggleAll, allCollapsed } = useCollapseManager();

    return allCollapsed !== null && <Button variant="outline-dark" onClick={toggleAll} {...props}><Icon intent={allCollapsed ? 'expand' : 'collapse'}/> {t(allCollapsed ? 'expandAll' : 'collapseAll')}</Button>
}